import React, { useState, useEffect, useRef } from "react";

interface Option {
  id: string;
  name: string;
}

interface CustomDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: Option) => void;
  placeholder: string;
  disabled?: boolean;
  icon: string;
  showOneLine?: boolean;
}

const CustomDropdownAssistant: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  icon,
  showOneLine = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selected = options.find((opt) => opt.id === value)?.name || placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative flex-1 flex" ref={dropdownRef}>
      <button
        type="button"
        className={`bg-[#FFFFFE] border flex-1 border-[#E4E7EC] text-gray-900 flex justify-between items-center space-x-1.5  font-medium p-2.5 rounded-md text-sm focus:outline-none disabled:text-gray-400 disabled:cursor-not-allowed`}
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
      >
        <div className="flex flex-1 space-x-1.5 items-center">
          <img src={icon} className="2xl:w-6 2xl:h-6 h-5 w-5" alt="" />
          <p className="text-ellipsis line-clamp-1 text-left mt-1 text-xs 2xl:text-sm">
            {selected}
          </p>
        </div>
        <img src="/arrow-down.svg" className="w-5" alt="" />
      </button>

      {isOpen && (
        <div className="absolute mt-1 w-52 2xl:w-64 bg-white rounded-md overflow-hidden shadow-lg z-50">
          <div className="max-h-60 overflow-y-auto scrollbar-hide">
            {options.map((opt) => (
              <div
                key={opt.id}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className={`px-4 py-2 cursor-pointer text-gray-900 text-xs 2xl:text-sm hover:bg-[#F2F4F7] ${
                  opt.id === value ? "bg-[#F2F4F7]" : ""
                }`}
              >
                <p
                  className={`${
                    showOneLine
                      ? "text-ellipsis line-clamp-1"
                      : "break-all whitespace-normal"
                  }`}
                >
                  {opt.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdownAssistant;
